import React, { useState, useMemo, useEffect } from "react";

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const ChevronDownIcon = ({ size = 20, color = "currentColor", style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={style}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const SearchIcon = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const PawIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="4" r="2" /><circle cx="18" cy="8" r="2" /><circle cx="20" cy="15.1" r="2" />
    <circle cx="3.8" cy="14" r="2" />
    <path d="M12 22s6-4 6-10a6 6 0 0 0-12 0c0 6 6 10 6 10z" />
  </svg>
);

/* ─── FAQ Data ───────────────────────────────────────────────────────────── */
const FAQ_CATEGORIES = ["All", "Grooming", "Pet Hotel", "Booking & Payment", "General"];

const FAQ_DATA = [
  // Grooming
  {
    category: "Grooming",
    question: "What grooming packages do you offer?",
    answer: "We offer Basic Grooming (bath, blowdry, brush, nail trim) starting at ₱400 for small dogs up to 7kg, and Full Grooming (basic + haircut & styling) starting at ₱500. Cat Grooming is a flat rate of ₱600 and includes a full bath, blowdry, nail trim, ear cleaning, and sanitary trim.",
  },
  {
    category: "Grooming",
    question: "What haircut styles are available for my dog?",
    answer: "For dogs we offer: Teddy Bear Cut, Puppy Cut, Lion Cut, Summer Cut, Natural Trim, and Breed-Specific Cuts. For cats: Lion Cut, Summer Cut, Comb Cut, and Natural Trim. You can include custom styling notes when booking!",
  },
  {
    category: "Grooming",
    question: "What add-on services can I include?",
    answer: "You can add: Nail Trim (₱100), Teeth Brushing (₱100 — please bring your pet's own toothbrush; a toothbrush can be provided for an additional fee if you don't have one), Eye Wash (₱100), Ear Cleaning (₱100), and Anal Drain (₱180). All add-ons are selectable during the online booking process.",
  },
  {
    category: "Grooming",
    question: "Does my pet need to be vaccinated before grooming or hotel stay?",
    answer: "Yes! Strict Store Policy: For the health and safety of all animals, dogs and cats that are not vaccinated (or do not have an up-to-date Anti-Rabies vaccination record) will NOT be accepted in the store. Please bring or upload your pet's vaccination card when booking or visiting.",
  },
  {
    category: "Grooming",
    question: "What if my pet has fleas or ticks?",
    answer: "We do NOT offer medicated baths. For the safety of all pets, we require that your pet be completely free of ticks and fleas before their grooming appointment. If your pet has an active infestation, please have them treated first before booking with us.",
  },
  // Pet Hotel
  {
    category: "Pet Hotel",
    question: "What are the pet hotel room options and rates?",
    answer: "We offer four room types: Dog Room Small for small & medium dogs at ₱500/night, Dog Room Large for large dogs at ₱800/night, Cat Villa Small — a cozy private villa for cats at ₱500/night, and Cat Villa Large — a spacious luxury villa for cats at ₱800/night. All stays include 24/7 supervision, custom feeding, and daily playtime updates!",
  },
  {
    category: "Pet Hotel",
    question: "What are the check-in and check-out times?",
    answer: "Check-in and check-out schedules are coordinated directly with our store. Please contact us to confirm your preferred drop-off and pick-up time when making your reservation.",
  },
  {
    category: "Pet Hotel",
    question: "Can I give special feeding instructions for my pet?",
    answer: "Absolutely! When booking a hotel stay, there's a dedicated field for feeding instructions. You can specify your pet's diet, meal times, allergies, medications, and any special care notes — our staff will follow them closely.",
  },
  // Booking & Payment
  {
    category: "Booking & Payment",
    question: "How do I book an appointment?",
    answer: "Booking is quick and easy! Click 'Book Appointment' in the menu, choose your service (Grooming, Pet Hotel, or Both), fill in your pet's details, select an available date and time, choose your payment method, sign the digital agreement, and submit. You'll receive a confirmation email shortly after!",
  },
  {
    category: "Booking & Payment",
    question: "What payment methods do you accept?",
    answer: "We accept GCash, UnionBank, BDO, and in-store Cash. You can choose to pay a 10% Downpayment to secure your reservation, or pay the full amount upfront. For Cash payments, no online reference number is needed. Please note: downpayments are NON-REFUNDABLE.",
  },
  {
    category: "Booking & Payment",
    question: "Can I reschedule or cancel my appointment?",
    answer: "Yes! You can request a reschedule or cancellation directly from your Customer Dashboard. Our admin team will review and process all requests promptly. Please try to notify us as early as possible to free up the time slot for other pets.",
  },
  // General
  {
    category: "General",
    question: "What are your business hours?",
    answer: "We are open Tuesday through Sunday from 9:00 AM to 7:00 PM. We are closed every Monday. Hotel guests may still check out on Mondays. We recommend booking in advance to guarantee your preferred time slot!",
  },
  {
    category: "General",
    question: "Where are you located?",
    answer: "Furpect Tails is located at 96 Gen. Luna St., Tuktukan, Taguig City. You can also reach us by phone at 0954 498 8342, or send a message through our Contact page or Live Chat.",
  },
  {
    category: "General",
    question: "Do you have a loyalty rewards program?",
    answer: "Yes! Every grooming visit earns you stamps on your Furpect Tails Loyalty Card. Reach 5 visits to unlock 20% off your next service, and 10 visits earns you a FREE grooming session! The loyalty card is linked to your account.",
  },
];

/* ─── AccordionItem ──────────────────────────────────────────────────────── */
function AccordionItem({ item, isOpen, onToggle, index }) {
  return (
    <div
      style={{
        background: "white",
        borderRadius: "1.2rem",
        overflow: "hidden",
        boxShadow: isOpen
          ? "0 8px 32px rgba(124,58,237,0.15)"
          : "0 2px 12px rgba(0,0,0,0.06)",
        border: `1.5px solid ${isOpen ? "rgba(124,58,237,0.3)" : "rgba(0,0,0,0.06)"}`,
        transition: "all 0.3s ease",
      }}
    >
      <button
        id={`faq-item-${index}`}
        onClick={onToggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
          padding: "1.25rem 1.5rem",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
        onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = "#faf5ff"; }}
        onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = "none"; }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1 }}>
          <span style={{
            minWidth: "2rem", height: "2rem", borderRadius: "0.6rem",
            background: isOpen ? "linear-gradient(135deg,#7C3AED,#EC4899)" : "rgba(124,58,237,0.08)",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.3s",
          }}>
            <PawIcon size={14} color={isOpen ? "white" : "#7C3AED"} />
          </span>
          <span style={{
            fontWeight: 700,
            fontSize: "1rem",
            color: isOpen ? "#7C3AED" : "#1f2937",
            transition: "color 0.3s",
            lineHeight: 1.4,
          }}>
            {item.question}
          </span>
        </div>
        <ChevronDownIcon
          size={20}
          color={isOpen ? "#7C3AED" : "#9CA3AF"}
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s", flexShrink: 0 }}
        />
      </button>

      <div style={{
        maxHeight: isOpen ? "400px" : "0",
        overflow: "hidden",
        transition: "max-height 0.4s ease",
      }}>
        <div style={{
          padding: "0 1.5rem 1.5rem 4.25rem",
          color: "#4B5563",
          fontSize: "0.95rem",
          lineHeight: 1.75,
          borderTop: "1px solid rgba(124,58,237,0.08)",
          paddingTop: "1rem",
          marginLeft: "1.5rem",
        }}>
          {item.answer}
        </div>
      </div>
    </div>
  );
}

/* ─── Main FAQ Component ─────────────────────────────────────────────────── */
const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const filteredFAQs = useMemo(() => {
    return FAQ_DATA.filter(item => {
      const matchesCategory = activeCategory === "All" || item.category === activeCategory;
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        !query ||
        item.question.toLowerCase().includes(query) ||
        item.answer.toLowerCase().includes(query);
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const handleToggle = (index) => {
    setOpenIndex(prev => (prev === index ? null : index));
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #faf5ff 0%, #fdf2f8 50%, #fff7ed 100%)" }}>
      {/* ── Header ── */}
      <section style={{ padding: "5rem 1.5rem 3rem", textAlign: "center", position: "relative", overflow: "hidden" }}>
        {/* Background orbs */}
        <div style={{ position: "absolute", top: "-4rem", left: "-4rem", width: "20rem", height: "20rem", borderRadius: "50%", background: "radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "0", right: "-3rem", width: "16rem", height: "16rem", borderRadius: "50%", background: "radial-gradient(circle, rgba(236,72,153,0.1) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1 }}>
          <span style={{
            display: "inline-block", background: "rgba(124,58,237,0.1)", color: "#7C3AED",
            fontSize: "0.8rem", fontWeight: 800, padding: "0.4rem 1rem", borderRadius: "999px",
            letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem",
          }}>
            Help Center
          </span>
          <h1 style={{
            fontSize: "clamp(2.2rem, 5vw, 3.5rem)", fontWeight: 900, color: "#1f2937",
            margin: "0 0 1rem", letterSpacing: "-0.02em", lineHeight: 1.1,
          }}>
            Frequently Asked{" "}
            <span style={{ background: "linear-gradient(90deg,#7C3AED,#EC4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Questions
            </span>
          </h1>
          <p style={{ fontSize: "1.1rem", color: "#6B7280", marginBottom: "2.5rem", maxWidth: "540px", margin: "0 auto 2.5rem" }}>
            Everything you need to know about Furpect Tails grooming, hotel, and booking.
          </p>

          {/* Search bar */}
          <div style={{
            maxWidth: "520px", margin: "0 auto",
            position: "relative",
          }}>
            <span style={{ position: "absolute", left: "1.1rem", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
              <SearchIcon size={18} color="#9CA3AF" />
            </span>
            <input
              id="faq-search"
              type="text"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setOpenIndex(null); }}
              style={{
                width: "100%",
                padding: "0.9rem 1rem 0.9rem 3rem",
                borderRadius: "1rem",
                border: "2px solid rgba(124,58,237,0.2)",
                fontSize: "1rem",
                background: "white",
                boxShadow: "0 4px 20px rgba(124,58,237,0.1)",
                outline: "none",
                transition: "border-color 0.2s",
                boxSizing: "border-box",
              }}
              onFocus={e => e.target.style.borderColor = "#7C3AED"}
              onBlur={e => e.target.style.borderColor = "rgba(124,58,237,0.2)"}
            />
          </div>
        </div>
      </section>

      {/* ── Category Tabs ── */}
      <div style={{ padding: "0 1.5rem 1.5rem", display: "flex", justifyContent: "center" }}>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "center" }}>
          {FAQ_CATEGORIES.map(cat => (
            <button
              key={cat}
              id={`faq-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => { setActiveCategory(cat); setOpenIndex(null); }}
              style={{
                padding: "0.5rem 1.2rem",
                borderRadius: "999px",
                border: "1.5px solid",
                fontSize: "0.88rem",
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.2s",
                borderColor: activeCategory === cat ? "#7C3AED" : "rgba(124,58,237,0.2)",
                background: activeCategory === cat ? "linear-gradient(135deg,#7C3AED,#EC4899)" : "white",
                color: activeCategory === cat ? "white" : "#7C3AED",
                boxShadow: activeCategory === cat ? "0 4px 15px rgba(124,58,237,0.3)" : "none",
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* ── FAQ List ── */}
      <section style={{ maxWidth: "780px", margin: "0 auto", padding: "0 1.5rem 6rem" }}>
        {filteredFAQs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "4rem 1rem" }}>
            <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🐾</div>
            <p style={{ fontSize: "1.1rem", color: "#9CA3AF", fontWeight: 600 }}>
              No questions found. Try a different keyword!
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {/* Category label */}
            {!searchQuery && activeCategory !== "All" && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                <span style={{ fontWeight: 800, color: "#7C3AED", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {activeCategory}
                </span>
                <div style={{ flex: 1, height: "1px", background: "rgba(124,58,237,0.15)" }} />
                <span style={{ color: "#9CA3AF", fontSize: "0.8rem", fontWeight: 600 }}>
                  {filteredFAQs.length} question{filteredFAQs.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
            {filteredFAQs.map((item, i) => (
              <AccordionItem
                key={`${item.category}-${i}`}
                item={item}
                index={i}
                isOpen={openIndex === i}
                onToggle={() => handleToggle(i)}
              />
            ))}
          </div>
        )}

        {/* Still have questions CTA */}
        <div style={{
          marginTop: "3rem",
          background: "linear-gradient(135deg,#7C3AED,#EC4899)",
          borderRadius: "1.5rem",
          padding: "2.5rem",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(124,58,237,0.25)",
        }}>
          <PawIcon size={32} color="rgba(255,255,255,0.8)" />
          <h3 style={{ fontWeight: 900, color: "white", fontSize: "1.4rem", margin: "0.75rem 0 0.5rem" }}>
            Still have questions?
          </h3>
          <p style={{ color: "rgba(255,255,255,0.8)", marginBottom: "1.5rem", fontSize: "0.95rem" }}>
            Our FurBot chatbot or live admin team is always here to help!
          </p>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("open-furbot"))}
              style={{
                background: "white", color: "#7C3AED", padding: "0.65rem 1.4rem",
                borderRadius: "0.8rem", fontWeight: 800, fontSize: "0.9rem",
                boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
                border: "none", cursor: "pointer",
                transition: "all 0.2s ease",
                display: "inline-flex", alignItems: "center", gap: "0.4rem"
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = "translateY(-2px) scale(1.03)";
                e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.2)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.15)";
              }}
            >
              💬 Chat with FurBot
            </button>
            <a
              href="tel:09544988342"
              style={{
                background: "rgba(255,255,255,0.15)", color: "white", padding: "0.65rem 1.4rem",
                borderRadius: "0.8rem", fontWeight: 800, fontSize: "0.9rem",
                border: "1.5px solid rgba(255,255,255,0.3)", textDecoration: "none",
                transition: "all 0.2s ease",
                display: "inline-flex", alignItems: "center", gap: "0.4rem"
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = "rgba(255,255,255,0.25)";
                e.currentTarget.style.transform = "translateY(-2px) scale(1.03)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = "rgba(255,255,255,0.15)";
                e.currentTarget.style.transform = "translateY(0) scale(1)";
              }}
            >
              📞 Call Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
